import { useCallback } from 'react';

import { useMixpanel } from 'context/mixpanel-context';
import { Package } from 'types';

export type TapToPayPage = 'retails' | 'misc_and_fees' | 'athlete_package';
export type BillingPlanType = 'single' | 'multi_installment';
export type CancelFromState = 'waiting' | 'failed' | 'timed_out';
export type GLMFlowDirection = 'kilo_to_glm' | 'glm_to_kilo';
export type ProgrammingCalendarView = 'month' | 'week' | 'day';

/**
 * Every view that mounts the shared invoice filter bar. Kept as a union so a
 * new mount cannot ship without naming itself, and so the values stay stable
 * for whoever reads the funnel later.
 */
export type InvoiceFilterSource =
  | 'invoices'
  | 'athlete-invoices'
  | 'athlete-invoices-mobile'
  | 'total-revenue'
  | 'upcoming-revenue'
  | 'pending-payments'
  | 'past-due-payments';

export interface StaffEventProperties {
  staff_glm_flow_created?: {
    flow_id: string;
    direction: GLMFlowDirection;
    trigger: string;
    action: string;
    enabled: boolean;
  };

  staff_glm_flow_updated?: {
    flow_id: string;
    direction: GLMFlowDirection;
    trigger: string;
    action: string;
    changed_fields: string[];
  };

  staff_glm_flow_deleted?: {
    flow_id: string;
    direction: GLMFlowDirection;
    trigger: string;
    action: string;
  };

  staff_glm_flow_toggled?: {
    flow_id: string;
    direction: GLMFlowDirection;
    trigger: string;
    action: string;
    enabled: boolean;
  };

  staff_athlete_glm_linked?: {
    athlete_id: string;
    source: 'search_modal' | 'create_contact';
  };

  staff_athlete_glm_unlinked?: {
    athlete_id: string;
  };

  staff_athlete_glm_contact_created?: {
    athlete_id: string;
  };

  staff_athlete_glm_link_retried?: {
    athlete_id: string;
  };

  staff_leaderboard_toggled?: {
    opened: boolean;
  };

  staff_leaderboard_filter_sheet_opened?: Record<string, never>;

  staff_leaderboard_date_filter_changed?: {
    value: 'this-session' | 'today';
  };

  staff_leaderboard_gender_filter_changed?: {
    value: 'all' | 'men' | 'women';
  };

  staff_leaderboard_category_filter_changed?: {
    value: string;
  };

  staff_leaderboard_filter_sheet_applied?: {
    date: 'this-session' | 'today';
    gender: 'all' | 'men' | 'women';
    level: string;
  };

  staff_leaderboard_entry_clicked?: {
    athlete_id: string;
    rank: number;
  };

  // The viewer's role is one of the base properties every event carries, so it is not
  // repeated here.
  staff_workout_history_viewed?: Record<string, never>;

  staff_workout_history_movement_opened?: {
    workout_movement_id: string;
    score_type: string;
  };

  staff_workout_history_workout_opened?: {
    workout_id: string;
  };

  staff_athlete_glm_link_icon_clicked?: {
    athlete_id: string;
    link_state: string;
  };

  staff_glm_records_modal_viewed?: {
    flow_id: string;
    direction: GLMFlowDirection;
  };

  staff_glm_records_modal_filtered?: {
    flow_id: string;
    direction: GLMFlowDirection;
    status: string;
  };

  staff_onboarding_checklist_step_updated?: {
    // The step's stable key, e.g. 'account_settings'. Never the display copy, which changes.
    step_key: string;
    checked: boolean;
  };

  staff_onboarding_checklist_banner_dismissed?: {
    steps_completed: number;
  };

  staff_athlete_selected?: {
    athlete_id: string;
    package_name: string | null;
  };

  staff_athlete_list_panel_toggled?: {
    state: 'collapsed' | 'expanded';
  };

  staff_calendar_view_changed?: {
    view: ProgrammingCalendarView;
    previous_view: ProgrammingCalendarView;
  };

  staff_assign_workout_opened?: {
    source: 'info_bar' | 'day_view' | 'week_view' | 'month_view' | 'change_workout';
  };

  staff_assign_date_changed?: {
    from_date: string;
    to_date: string;
  };

  staff_assign_workout_confirmed?: {
    template_id: string | null;
    date: string;
    visible_to_athlete: boolean;
  };

  staff_assign_workout_cancelled?: Record<string, never>;

  staff_workout_card_clicked?: {
    workout_id: string;
  };

  staff_workout_visibility_quick_toggled?: {
    workout_id: string;
    new_state: 'visible' | 'hidden';
    view: ProgrammingCalendarView;
  };

  staff_workout_visibility_modal_toggled?: {
    workout_id: string;
    new_state: 'visible' | 'hidden';
  };

  staff_workout_edit_clicked?: {
    workout_id: string;
    source: 'menu' | 'modal';
  };

  staff_workout_deleted?: {
    workout_id: string;
  };

  staff_stripe_dashboard_viewed?: {
    // 'custom' | 'standard' — never null here, the page is unreachable without a connect account
    stripe_account_type: string;
    // Whether the viewer sees the owner-only cards or the staff carve-out
    is_owner: boolean;
  };

  athlete_created?: {
    athlete_id: string;
    athlete_name: string;
    member_since: string | null;
  };

  staff_kiosk_checkin?: {
    athlete_id: string;
    athlete_name: string;
    event_id: string;
    event_name: string;
    checkin_method: string; // staff_assisted or athlete_self_service, the last one is when user enters athlete mode
  };

  //Just to track how many users have accessed the kiosk mode. User properties are already tracked as a part of the session.
  staff_kiosk_accessed?: {};

  staff_event_created?: {
    event_id: string;
    event_name: string;
    event_type: string;
    capacity: number;
    event_time: string;
    event_location: string;
    event_duration: number;
    is_recurring: boolean;
    event_type_module: string; // class or appointment
  };

  staff_event_updated?: {
    event_id: string;
    event_type: string;
    event_name: string;
    event_type_module: string; // class or appointment
    event_update_type: string; // all future events, this day only
    field_changed: string[]; // ['time', 'capacity', 'instructor']
  };

  // Bulk multi-row creation analytics (PROD-104). Ids + counts only — no athlete PII.
  staff_event_bulk_created?: {
    event_type_module: string; // class or appointment
    event_type: string; // human-readable type name
    event_type_id: string; // joins back to the appointment/class type
    session_count: number; // rows submitted
    created_count: number; // rows that persisted
    failure_count: number; // rows that failed (partial-failure rate)
    created_ids: string[]; // ids of the events produced (entity ids, not PII)
  };

  staff_event_session_row_changed?: {
    event_type_module: string; // class or appointment
    action: 'added' | 'removed';
    session_count: number; // count after the change
  };

  staff_event_create_validation_failed?: {
    event_type_module: string; // class or appointment
    error_fields: string[]; // field keys that failed validation (no values)
    session_count: number;
    is_recurring: boolean;
  };

  staff_event_create_abandoned?: {
    event_type_module: string; // class or appointment
    session_count: number;
    is_recurring: boolean;
  };

  staff_calendar_filter_applied?: {
    filter_type: string;
    filter_value: string;
  };

  /**
   * One event for every invoice-table filter, on every view that renders the
   * shared filter bar. `filter_type` carries which control was used, so a new
   * invoice filter reuses this event instead of adding another one.
   */
  staff_invoice_filter_applied?: {
    filter_type: string;
    filter_value: string;
    source: InvoiceFilterSource;
  };

  staff_athlete_reserved?: {
    athlete_id: string;
    athlete_name: string;
    event_id: string;
    event_name: string;
    event_type_module: string; // class or appointment
  };

  //another event just because has different properties than kiosk checkin
  staff_athlete_checked_in?: {
    athlete_id: string;
    athlete_name: string;
    event_id: string;
    event_name: string;
    event_type_module: string; // class or appointment
  };

  staff_athlete_late_cancel_no_show?: {
    athlete_id: string;
    athlete_name: string;
    event_id: string;
    event_name: string;
    event_type_module: string; // class or appointment
    attendance_status: string; // late cancel, no show
    triggered_by: 'kiosk' | 'calendar'; // kiosk or calendar
    fee_waived: boolean;
    charge_now: boolean;
    credit_deducted: boolean;
    fee_amount: number;
    time_open_seconds: number;
  };

  staff_late_cancel_no_show_modal_opened?: {
    athlete_id: string;
    athlete_name: string;
    event_id: string;
    event_name: string;
    event_type_module: string; // class or appointment
    action_type: string; // late cancel or no show
    triggered_by: 'kiosk' | 'calendar';
  };

  staff_late_cancel_no_show_modal_dismissed?: {
    athlete_id: string;
    event_id: string;
    event_type_module: string;
    action_type: string; // late cancel or no show
    time_open_seconds: number;
  };

  staff_package_created?: {
    package_id: string;
    package_name: string;
    package_type: Package['type'];
    is_renewal: boolean;
    is_one_time_purchase: boolean;
    package_duration: number | null;
    package_duration_unit: string | null;
    athlete_status_on_start: string;
    athlete_status_on_end: string;
  };

  staff_product_sale_purchased?: {
    transaction_id: string;
    athlete_id: string;
    athlete_name: string;
    items_purchased: number;
    payment_method: string;
    total: number;
    product_type_module: string; // retail or free misc products
  };

  staff_athlete_updated?: {
    athlete_id: string;
    athlete_name: string;
    field_changed: string[];
  };

  staff_athlete_package_created?: {
    athlete_id: string;
    package_id: string;
    package_name: string;
    package_type: Package['type'];
    autorenew: boolean;
    athlete_status_on_start: string;
    athlete_status_on_end: string;
  };

  staff_athlete_package_auto_pay_updated?: {
    athlete_package_id: string;
    auto_pay: boolean;
    // How many packages in the chain the change actually reached, and how many it could not.
    // The reach is the feature's own success measure: nothing recorded it before.
    packages_updated: number;
    packages_skipped: number;
    skipped_reasons: string[];
  };

  staff_invoice_apply_scope_selected?: {
    invoice_id: string;
    // Which of the two options staff picked at the prompt. The split between them is the main
    // evidence for whether the prompt should exist at all.
    scope: 'invoice_only' | 'package';
    fields_changed: string[];
    // How many packages the change reached, and why it reached none. Zero with no refusal code
    // means staff kept the edit to the invoice.
    packages_updated: number;
    refusal_code: string | null;
  };

  staff_payment_method_auto_pay_selected?: {
    athlete_id: string;
    // How many packages the toggle stood to reach, so the events say what staff were choosing
    // between rather than only how often they said yes.
    package_count: number;
    auto_pay_enabled: boolean;
  };

  staff_invoice_voided?: {
    invoice_id: string;
    athlete_id: string;
    amount_voided: number;
  };

  staff_invoice_payment_date_edited?: {
    invoice_id: string;
    payment_method: string | null;
    // Signed, negative when the date moved earlier. Shows how far off the registered dates
    // typically are.
    days_moved: number;
    // Whether the correction crossed a reporting period, which is the accountant's problem this
    // feature exists for.
    crossed_month: boolean;
  };

  staff_discount_created?: {
    discount_id: string;
    discount_name: string;
    discount_code: string;
    value_type: 'PERCENTAGE' | 'FIXED_AMOUNT';
    is_active: boolean;
    is_one_time_use: boolean;
    eligible_products_count: number;
  };

  staff_discount_updated?: {
    discount_id: string;
    discount_name: string;
    field_changed: string[];
  };

  staff_discount_archived?: {
    discount_id: string;
    discount_name: string;
  };

  staff_discount_unarchived?: {
    discount_id: string;
    discount_name: string;
  };

  staff_check_in_program_created?: {
    check_in_program_id: string;
    current_threshold: number;
    athlete_visibility_enabled: boolean;
  };

  staff_check_in_program_settings_updated?: {
    check_in_program_id: string;
    current_threshold: number;
    athlete_visibility_enabled: boolean;
    changed_fields: string[];
  };

  staff_check_in_program_tracking_viewed?: Record<string, never>;

  staff_check_in_program_tracking_filter_applied?: {
    filter_name: string;
    filter_value: string;
  };

  staff_check_in_program_tracking_csv_downloaded?: {
    month: string;
  };

  staff_discount_usage_report_viewed?: {
    discount_id: string;
    discount_name: string;
    total_uses: number;
    total_athletes: number;
    total_saved: number;
  };

  staff_discount_applied?: {
    discount_id: string;
    discount_name: string;
    value_type: 'PERCENTAGE' | 'FIXED_AMOUNT';
    applied_to: 'invoice_item' | 'athlete_package';
  };

  staff_tap_to_pay_selected?: {
    page: TapToPayPage;
    is_charge_now: boolean;
    reader_online: boolean;
    package_id?: string;
    billing_plan_type?: BillingPlanType;
    installment_count?: number;
    auto_pay_enabled?: boolean;
  };

  staff_tap_to_pay_collection_started?: {
    page: TapToPayPage;
    invoice_id: string;
    athlete_id: string;
    amount: number;
    item_count: number;
    save_card: boolean;
  };

  staff_tap_to_pay_payment_succeeded?: {
    page: TapToPayPage;
    invoice_id: string;
    athlete_id: string;
    amount: number;
    item_count: number;
    save_card: boolean;
  };

  staff_tap_to_pay_payment_failed?: {
    page: TapToPayPage;
    invoice_id: string;
    failure_code: string | null;
  };

  staff_tap_to_pay_card_save_started?: {
    page: 'retails' | 'athlete_package';
    invoice_id: string;
    athlete_id: string;
    billing_start_date: string;
  };

  staff_tap_to_pay_card_saved?: {
    page: TapToPayPage;
    invoice_id: string;
    athlete_id: string;
  };

  staff_tap_to_pay_card_save_failed?: {
    page: TapToPayPage;
    invoice_id: string;
    failure_code: string | null;
  };

  staff_tap_to_pay_cancelled?: {
    page: TapToPayPage;
    invoice_id: string;
    from_state: CancelFromState;
  };

  staff_tap_to_pay_change_payment_method_clicked?: {
    page: TapToPayPage;
    invoice_id: string;
  };

  staff_tap_to_pay_retry_clicked?: {
    page: TapToPayPage;
    invoice_id: string;
    retry_attempt: number;
  };

  staff_tap_to_pay_polling_timed_out?: {
    page: TapToPayPage;
    invoice_id: string;
  };

  staff_card_reader_purchase_clicked?: {};

  staff_card_reader_registered?: {
    device_type: string;
  };

  staff_card_reader_registration_failed?: {
    failure_code: string | null;
  };

  staff_ai_connect_authorize_viewed?: {};

  staff_ai_connect_authorized?: {
    gym_id: string;
    gym_count: number;
  };

  staff_ai_connect_denied?: {};

  staff_ai_connect_revoked?: {
    connection_name: string;
  };

  staff_reservation_alert_badge_viewed?: {
    badge: 'visitor' | 'first_class_visit';
    surface: 'scheduling' | 'kiosk';
    athlete_id?: string;
  };

  staff_pay_rate_created?: {
    pay_rate_id: string;
    pay_rate_type: 'base' | 'hourly' | 'percentage';
    percentage: number | null;
    additional_per_attendee: boolean;
    include_no_shows: boolean;
    include_late_cancels: boolean;
  };

  staff_pay_rate_updated?: {
    pay_rate_id: string;
    pay_rate_type: 'base' | 'hourly' | 'percentage';
    fields_changed: string[];
    include_no_shows: boolean;
    include_late_cancels: boolean;
  };

  staff_total_session_value_breakdown_viewed?: {
    pay_rate_type: 'percentage';
    athletes_total: number;
    athletes_checked_in: number;
    athletes_late_cancel: number;
    athletes_no_show: number;
    total_session_value: number;
    payout_amount: number;
  };
}

export const useStaffTracking = (): any => {
  const { track, startSessionRecording } = useMixpanel();

  const trackAthleteCreate = useCallback(
    (athleteId: string, athleteName: string, memberSince: string | null) => {
      track('staff_athlete_created', {
        athlete_created: {
          athlete_id: athleteId,
          athlete_name: athleteName,
          member_since: memberSince,
        },
      });
    },
    [track],
  );

  const trackKioskCheckin = useCallback(
    (athleteId: string, athleteName: string, eventId: string, eventName: string, checkinMethod: string) => {
      track('staff_kiosk_checkin', {
        staff_kiosk_checkin: {
          athlete_id: athleteId,
          athlete_name: athleteName,
          event_id: eventId,
          event_name: eventName,
          checkin_method: checkinMethod,
        },
      });
    },
    [track],
  );

  const trackKioskAccessed = useCallback(() => {
    track('staff_kiosk_accessed', {
      staff_kiosk_accessed: {},
    });
  }, [track]);

  const trackEventCreate = useCallback(
    (
      eventId: string,
      eventName: string,
      eventType: string,
      capacity: number,
      eventTime: string,
      eventLocation: string,
      eventDuration: number,
      isRecurring: boolean,
      eventTypeModule: string,
    ) => {
      track('staff_event_created', {
        staff_event_created: {
          event_id: eventId,
          event_name: eventName,
          event_type: eventType,
          capacity: capacity,
          event_time: eventTime,
          event_location: eventLocation,
          event_duration: eventDuration,
          is_recurring: isRecurring,
          event_type_module: eventTypeModule,
        },
      });
    },
    [track],
  );

  const trackEventUpdate = useCallback(
    (
      eventId: string,
      eventType: string,
      eventName: string,
      eventTypeModule: string,
      eventUpdateType: string,
      fieldChangedKeys: string[],
    ) => {
      track('staff_event_updated', {
        staff_event_updated: {
          event_id: eventId,
          event_type: eventType,
          event_name: eventName,
          event_type_module: eventTypeModule,
          event_update_type: eventUpdateType,
          field_changed: fieldChangedKeys,
        },
      });
    },
    [track],
  );

  const trackEventBulkCreated = useCallback(
    (props: NonNullable<StaffEventProperties['staff_event_bulk_created']>) => {
      track('staff_event_bulk_created', { staff_event_bulk_created: props });
    },
    [track],
  );

  const trackEventSessionRowChanged = useCallback(
    (props: NonNullable<StaffEventProperties['staff_event_session_row_changed']>) => {
      track('staff_event_session_row_changed', { staff_event_session_row_changed: props });
    },
    [track],
  );

  const trackEventCreateValidationFailed = useCallback(
    (props: NonNullable<StaffEventProperties['staff_event_create_validation_failed']>) => {
      track('staff_event_create_validation_failed', { staff_event_create_validation_failed: props });
    },
    [track],
  );

  const trackEventCreateAbandoned = useCallback(
    (props: NonNullable<StaffEventProperties['staff_event_create_abandoned']>) => {
      track('staff_event_create_abandoned', { staff_event_create_abandoned: props });
    },
    [track],
  );

  const trackcalendarFilterApplied = useCallback(
    (filterType: string, filterValue: string) => {
      track('staff_calendar_filter_applied', {
        staff_calendar_filter_applied: {
          filter_type: filterType,
          filter_value: filterValue,
        },
      });
    },
    [track],
  );

  const trackInvoiceFilterApplied = useCallback(
    (filterType: string, filterValue: string, source: InvoiceFilterSource) => {
      track('staff_invoice_filter_applied', {
        staff_invoice_filter_applied: {
          filter_type: filterType,
          filter_value: filterValue,
          source,
        },
      });
    },
    [track],
  );

  const trackAthleteReserved = useCallback(
    (
      athleteId: string,
      athleteName: string,
      eventId: string,
      eventName: string,
      eventTypeModule: string,
    ) => {
      track('staff_athlete_reserved', {
        staff_athlete_reserved: {
          athlete_id: athleteId,
          athlete_name: athleteName,
          event_id: eventId,
          event_name: eventName,
          event_type_module: eventTypeModule,
        },
      });
    },
    [track],
  );

  const trackAthleteCheckedIn = useCallback(
    (
      athleteId: string,
      athleteName: string,
      eventId: string,
      eventName: string,
      eventTypeModule: string,
    ) => {
      track('staff_athlete_checked_in', {
        staff_athlete_checked_in: {
          athlete_id: athleteId,
          athlete_name: athleteName,
          event_id: eventId,
          event_name: eventName,
          event_type_module: eventTypeModule,
        },
      });
    },
    [track],
  );

  const trackAthleteLateCancelNoShow = useCallback(
    (
      athleteId: string,
      athleteName: string,
      eventId: string,
      eventName: string,
      eventTypeModule: string,
      attendanceStatus: string,
      triggeredBy: string,
      feeWaived: boolean,
      chargeNow: boolean,
      creditDeducted: boolean,
      feeAmount: number,
      timeOpenSeconds: number,
    ) => {
      track('staff_athlete_late_cancel_no_show', {
        staff_athlete_late_cancel_no_show: {
          athlete_id: athleteId,
          athlete_name: athleteName,
          event_id: eventId,
          event_name: eventName,
          event_type_module: eventTypeModule,
          attendance_status: attendanceStatus,
          triggered_by: triggeredBy,
          fee_waived: feeWaived,
          charge_now: chargeNow,
          credit_deducted: creditDeducted,
          fee_amount: feeAmount,
          time_open_seconds: timeOpenSeconds,
        },
      });
    },
    [track],
  );

  const trackReservationAlertBadgeViewed = useCallback(
    (props: NonNullable<StaffEventProperties['staff_reservation_alert_badge_viewed']>) => {
      track('staff_reservation_alert_badge_viewed', {
        staff_reservation_alert_badge_viewed: props,
      });
    },
    [track],
  );

  const trackLateCancelNoShowModalOpened = useCallback(
    (
      athleteId: string,
      athleteName: string,
      eventId: string,
      eventName: string,
      eventTypeModule: string,
      actionType: string,
      triggeredBy: 'kiosk' | 'calendar' | 'attendance',
    ) => {
      track('staff_late_cancel_no_show_modal_opened', {
        staff_late_cancel_no_show_modal_opened: {
          athlete_id: athleteId,
          athlete_name: athleteName,
          event_id: eventId,
          event_name: eventName,
          event_type_module: eventTypeModule,
          action_type: actionType,
          triggered_by: triggeredBy,
        },
      });
    },
    [track],
  );

  const trackLateCancelNoShowModalDismissed = useCallback(
    (
      athleteId: string,
      eventId: string,
      eventTypeModule: string,
      actionType: string,
      timeOpenSeconds: number,
    ) => {
      track('staff_late_cancel_no_show_modal_dismissed', {
        staff_late_cancel_no_show_modal_dismissed: {
          athlete_id: athleteId,
          event_id: eventId,
          event_type_module: eventTypeModule,
          action_type: actionType,
          time_open_seconds: timeOpenSeconds,
        },
      });
    },
    [track],
  );

  const trackPackageCreated = useCallback(
    (
      packageId: string,
      packageName: string,
      packageType: Package['type'],
      isRenewal: boolean,
      isOneTimePurchase: boolean,
      packageDuration: number | null,
      packageDurationUnit: string | null,
      athleteStatusOnStart: string,
      athleteStatusOnEnd: string,
    ) => {
      track('staff_package_created', {
        staff_package_created: {
          package_id: packageId,
          package_name: packageName,
          package_type: packageType,
          is_renewal: isRenewal,
          is_one_time_purchase: isOneTimePurchase,
          package_duration: packageDuration,
          package_duration_unit: packageDurationUnit,
          athlete_status_on_start: athleteStatusOnStart,
          athlete_status_on_end: athleteStatusOnEnd,
        },
      });
    },
    [track],
  );

  const trackProductSalePurchased = useCallback(
    (
      transactionId: string,
      athleteId: string,
      athleteName: string,
      itemsPurchased: number,
      paymentMethod: string,
      total: number,
      productTypeModule: string,
    ) => {
      track('staff_product_sale_purchased', {
        staff_product_sale_purchased: {
          transaction_id: transactionId,
          athlete_id: athleteId,
          athlete_name: athleteName,
          items_purchased: itemsPurchased,
          payment_method: paymentMethod,
          total: total,
          product_type_module: productTypeModule,
        },
      });
    },
    [track],
  );

  const trackAthleteUpdated = useCallback(
    (athleteId: string, athleteName: string, fieldChangedKeys: string[]) => {
      track('staff_athlete_updated', {
        staff_athlete_updated: {
          athlete_id: athleteId,
          athlete_name: athleteName,
          field_changed: fieldChangedKeys,
        },
      });
    },
    [track],
  );

  const trackAthletePackageCreated = useCallback(
    (
      athleteId: string,
      packageId: string,
      packageName: string,
      packageType: Package['type'],
      autorenew: boolean,
      athleteStatusOnStart: string,
      athleteStatusOnEnd: string,
    ) => {
      track('staff_athlete_package_created', {
        staff_athlete_package_created: {
          athlete_id: athleteId,
          package_id: packageId,
          package_name: packageName,
          package_type: packageType,
          autorenew: autorenew,
          athlete_status_on_start: athleteStatusOnStart,
          athlete_status_on_end: athleteStatusOnEnd,
        },
      });
    },
    [track],
  );

  const trackAthletePackageAutoPayUpdated = useCallback(
    (
      athletePackageId: string,
      autoPay: boolean,
      packagesUpdated: number,
      packagesSkipped: number,
      skippedReasons: string[],
    ) => {
      track('staff_athlete_package_auto_pay_updated', {
        staff_athlete_package_auto_pay_updated: {
          athlete_package_id: athletePackageId,
          auto_pay: autoPay,
          packages_updated: packagesUpdated,
          packages_skipped: packagesSkipped,
          skipped_reasons: skippedReasons,
        },
      });
    },
    [track],
  );

  const trackInvoiceApplyScopeSelected = useCallback(
    (
      invoiceId: string,
      scope: 'invoice_only' | 'package',
      fieldsChanged: string[],
      packagesUpdated: number,
      refusalCode: string | null,
    ) => {
      track('staff_invoice_apply_scope_selected', {
        staff_invoice_apply_scope_selected: {
          invoice_id: invoiceId,
          scope,
          fields_changed: fieldsChanged,
          packages_updated: packagesUpdated,
          refusal_code: refusalCode,
        },
      });
    },
    [track],
  );

  const trackPaymentMethodAutoPaySelected = useCallback(
    (athleteId: string, packageCount: number, autoPayEnabled: boolean) => {
      track('staff_payment_method_auto_pay_selected', {
        staff_payment_method_auto_pay_selected: {
          athlete_id: athleteId,
          package_count: packageCount,
          auto_pay_enabled: autoPayEnabled,
        },
      });
    },
    [track],
  );

  const trackInvoicePaymentDateEdited = useCallback(
    (invoiceId: string, paymentMethod: string | null, daysMoved: number, crossedMonth: boolean) => {
      track('staff_invoice_payment_date_edited', {
        staff_invoice_payment_date_edited: {
          invoice_id: invoiceId,
          payment_method: paymentMethod,
          days_moved: daysMoved,
          crossed_month: crossedMonth,
        },
      });
    },
    [track],
  );

  const trackInvoiceVoided = useCallback(
    (invoiceId: string, athleteId: string, amountVoided: number) => {
      track('staff_invoice_voided', {
        staff_invoice_voided: {
          invoice_id: invoiceId,
          athlete_id: athleteId,
          amount_voided: amountVoided,
        },
      });
    },
    [track],
  );

  // Discounts

  const trackDiscountCreated = useCallback(
    (
      discountId: string,
      discountName: string,
      discountCode: string,
      valueType: 'PERCENTAGE' | 'FIXED_AMOUNT',
      isActive: boolean,
      isOneTimeUse: boolean,
      eligibleProductsCount: number,
    ) => {
      track('staff_discount_created', {
        staff_discount_created: {
          discount_id: discountId,
          discount_name: discountName,
          discount_code: discountCode,
          value_type: valueType,
          is_active: isActive,
          is_one_time_use: isOneTimeUse,
          eligible_products_count: eligibleProductsCount,
        },
      });
    },
    [track],
  );

  const trackDiscountUpdated = useCallback(
    (discountId: string, discountName: string, fieldChangedKeys: string[]) => {
      track('staff_discount_updated', {
        staff_discount_updated: {
          discount_id: discountId,
          discount_name: discountName,
          field_changed: fieldChangedKeys,
        },
      });
    },
    [track],
  );

  const trackCheckInProgramCreated = useCallback(
    (checkInProgramId: string, currentThreshold: number, athleteVisibilityEnabled: boolean) => {
      track('staff_check_in_program_created', {
        staff_check_in_program_created: {
          check_in_program_id: checkInProgramId,
          current_threshold: currentThreshold,
          athlete_visibility_enabled: athleteVisibilityEnabled,
        },
      });
    },
    [track],
  );

  const trackCheckInProgramTrackingViewed = useCallback(() => {
    track('staff_check_in_program_tracking_viewed', {
      staff_check_in_program_tracking_viewed: {},
    });
  }, [track]);

  const trackCheckInProgramTrackingFilterApplied = useCallback(
    (filterName: string, filterValue: string) => {
      track('staff_check_in_program_tracking_filter_applied', {
        staff_check_in_program_tracking_filter_applied: {
          filter_name: filterName,
          filter_value: filterValue,
        },
      });
    },
    [track],
  );

  const trackCheckInProgramTrackingCsvDownloaded = useCallback(
    (month: string) => {
      track('staff_check_in_program_tracking_csv_downloaded', {
        staff_check_in_program_tracking_csv_downloaded: { month },
      });
    },
    [track],
  );

  const trackCheckInProgramSettingsUpdated = useCallback(
    (
      checkInProgramId: string,
      currentThreshold: number,
      athleteVisibilityEnabled: boolean,
      changedFields: string[],
    ) => {
      track('staff_check_in_program_settings_updated', {
        staff_check_in_program_settings_updated: {
          check_in_program_id: checkInProgramId,
          current_threshold: currentThreshold,
          athlete_visibility_enabled: athleteVisibilityEnabled,
          changed_fields: changedFields,
        },
      });
    },
    [track],
  );

  const trackDiscountArchived = useCallback(
    (discountId: string, discountName: string) => {
      track('staff_discount_archived', {
        staff_discount_archived: {
          discount_id: discountId,
          discount_name: discountName,
        },
      });
    },
    [track],
  );

  const trackDiscountUnarchived = useCallback(
    (discountId: string, discountName: string) => {
      track('staff_discount_unarchived', {
        staff_discount_unarchived: {
          discount_id: discountId,
          discount_name: discountName,
        },
      });
    },
    [track],
  );

  const trackDiscountUsageReportViewed = useCallback(
    (
      discountId: string,
      discountName: string,
      totalUses: number,
      totalAthletes: number,
      totalSaved: number,
    ) => {
      track('staff_discount_usage_report_viewed', {
        staff_discount_usage_report_viewed: {
          discount_id: discountId,
          discount_name: discountName,
          total_uses: totalUses,
          total_athletes: totalAthletes,
          total_saved: totalSaved,
        },
      });
    },
    [track],
  );

  const trackDiscountApplied = useCallback(
    (
      discountId: string,
      discountName: string,
      valueType: 'PERCENTAGE' | 'FIXED_AMOUNT',
      appliedTo: 'invoice_item' | 'athlete_package',
    ) => {
      track('staff_discount_applied', {
        staff_discount_applied: {
          discount_id: discountId,
          discount_name: discountName,
          value_type: valueType,
          applied_to: appliedTo,
        },
      });
    },
    [track],
  );

  // Tap-to-Pay

  const trackTapToPaySelected = useCallback(
    (props: NonNullable<StaffEventProperties['staff_tap_to_pay_selected']>) => {
      track('staff_tap_to_pay_selected', { staff_tap_to_pay_selected: props });
      startSessionRecording();
    },
    [track, startSessionRecording],
  );

  const trackTapToPayCollectionStarted = useCallback(
    (props: NonNullable<StaffEventProperties['staff_tap_to_pay_collection_started']>) => {
      track('staff_tap_to_pay_collection_started', { staff_tap_to_pay_collection_started: props });
    },
    [track],
  );

  const trackTapToPayPaymentSucceeded = useCallback(
    (props: NonNullable<StaffEventProperties['staff_tap_to_pay_payment_succeeded']>) => {
      track('staff_tap_to_pay_payment_succeeded', { staff_tap_to_pay_payment_succeeded: props });
    },
    [track],
  );

  const trackTapToPayPaymentFailed = useCallback(
    (props: NonNullable<StaffEventProperties['staff_tap_to_pay_payment_failed']>) => {
      track('staff_tap_to_pay_payment_failed', { staff_tap_to_pay_payment_failed: props });
    },
    [track],
  );

  const trackTapToPayCardSaveStarted = useCallback(
    (props: NonNullable<StaffEventProperties['staff_tap_to_pay_card_save_started']>) => {
      track('staff_tap_to_pay_card_save_started', { staff_tap_to_pay_card_save_started: props });
    },
    [track],
  );

  const trackTapToPayCardSaved = useCallback(
    (props: NonNullable<StaffEventProperties['staff_tap_to_pay_card_saved']>) => {
      track('staff_tap_to_pay_card_saved', { staff_tap_to_pay_card_saved: props });
    },
    [track],
  );

  const trackTapToPayCardSaveFailed = useCallback(
    (props: NonNullable<StaffEventProperties['staff_tap_to_pay_card_save_failed']>) => {
      track('staff_tap_to_pay_card_save_failed', { staff_tap_to_pay_card_save_failed: props });
    },
    [track],
  );

  const trackTapToPayCancelled = useCallback(
    (props: NonNullable<StaffEventProperties['staff_tap_to_pay_cancelled']>) => {
      track('staff_tap_to_pay_cancelled', { staff_tap_to_pay_cancelled: props });
    },
    [track],
  );

  const trackTapToPayChangePaymentMethodClicked = useCallback(
    (props: NonNullable<StaffEventProperties['staff_tap_to_pay_change_payment_method_clicked']>) => {
      track('staff_tap_to_pay_change_payment_method_clicked', {
        staff_tap_to_pay_change_payment_method_clicked: props,
      });
    },
    [track],
  );

  const trackTapToPayRetryClicked = useCallback(
    (props: NonNullable<StaffEventProperties['staff_tap_to_pay_retry_clicked']>) => {
      track('staff_tap_to_pay_retry_clicked', { staff_tap_to_pay_retry_clicked: props });
    },
    [track],
  );

  const trackTapToPayPollingTimedOut = useCallback(
    (props: NonNullable<StaffEventProperties['staff_tap_to_pay_polling_timed_out']>) => {
      track('staff_tap_to_pay_polling_timed_out', { staff_tap_to_pay_polling_timed_out: props });
    },
    [track],
  );

  // Card reader registration

  const trackCardReaderPurchaseClicked = useCallback(() => {
    track('staff_card_reader_purchase_clicked', { staff_card_reader_purchase_clicked: {} });
  }, [track]);

  const trackCardReaderRegistered = useCallback(
    (props: NonNullable<StaffEventProperties['staff_card_reader_registered']>) => {
      track('staff_card_reader_registered', { staff_card_reader_registered: props });
    },
    [track],
  );

  const trackCardReaderRegistrationFailed = useCallback(
    (props: NonNullable<StaffEventProperties['staff_card_reader_registration_failed']>) => {
      track('staff_card_reader_registration_failed', { staff_card_reader_registration_failed: props });
    },
    [track],
  );

  const trackAIConnectAuthorizeViewed = useCallback(() => {
    track('staff_ai_connect_authorize_viewed', { staff_ai_connect_authorize_viewed: {} });
  }, [track]);

  const trackAIConnectAuthorized = useCallback(
    (props: NonNullable<StaffEventProperties['staff_ai_connect_authorized']>) => {
      track('staff_ai_connect_authorized', { staff_ai_connect_authorized: props });
    },
    [track],
  );

  const trackAIConnectDenied = useCallback(() => {
    track('staff_ai_connect_denied', { staff_ai_connect_denied: {} });
  }, [track]);

  const trackAIConnectRevoked = useCallback(
    (props: NonNullable<StaffEventProperties['staff_ai_connect_revoked']>) => {
      track('staff_ai_connect_revoked', { staff_ai_connect_revoked: props });
    },
    [track],
  );

  const trackLeaderboardToggled = useCallback(
    (props: NonNullable<StaffEventProperties['staff_leaderboard_toggled']>) => {
      track('staff_leaderboard_toggled', { staff_leaderboard_toggled: props });
    },
    [track],
  );

  const trackLeaderboardFilterSheetOpened = useCallback(() => {
    track('staff_leaderboard_filter_sheet_opened', { staff_leaderboard_filter_sheet_opened: {} });
  }, [track]);

  const trackLeaderboardDateFilterChanged = useCallback(
    (props: NonNullable<StaffEventProperties['staff_leaderboard_date_filter_changed']>) => {
      track('staff_leaderboard_date_filter_changed', { staff_leaderboard_date_filter_changed: props });
    },
    [track],
  );

  const trackLeaderboardGenderFilterChanged = useCallback(
    (props: NonNullable<StaffEventProperties['staff_leaderboard_gender_filter_changed']>) => {
      track('staff_leaderboard_gender_filter_changed', { staff_leaderboard_gender_filter_changed: props });
    },
    [track],
  );

  const trackLeaderboardCategoryFilterChanged = useCallback(
    (props: NonNullable<StaffEventProperties['staff_leaderboard_category_filter_changed']>) => {
      track('staff_leaderboard_category_filter_changed', {
        staff_leaderboard_category_filter_changed: props,
      });
    },
    [track],
  );

  const trackLeaderboardFilterSheetApplied = useCallback(
    (props: NonNullable<StaffEventProperties['staff_leaderboard_filter_sheet_applied']>) => {
      track('staff_leaderboard_filter_sheet_applied', { staff_leaderboard_filter_sheet_applied: props });
    },
    [track],
  );

  const trackLeaderboardEntryClicked = useCallback(
    (props: NonNullable<StaffEventProperties['staff_leaderboard_entry_clicked']>) => {
      track('staff_leaderboard_entry_clicked', { staff_leaderboard_entry_clicked: props });
    },
    [track],
  );

  const trackWorkoutHistoryViewed = useCallback(() => {
    track('staff_workout_history_viewed', { staff_workout_history_viewed: {} });
  }, [track]);

  const trackWorkoutHistoryMovementOpened = useCallback(
    (props: NonNullable<StaffEventProperties['staff_workout_history_movement_opened']>) => {
      track('staff_workout_history_movement_opened', { staff_workout_history_movement_opened: props });
    },
    [track],
  );

  const trackWorkoutHistoryWorkoutOpened = useCallback(
    (props: NonNullable<StaffEventProperties['staff_workout_history_workout_opened']>) => {
      track('staff_workout_history_workout_opened', { staff_workout_history_workout_opened: props });
    },
    [track],
  );

  const trackGLMFlowCreated = useCallback(
    (props: NonNullable<StaffEventProperties['staff_glm_flow_created']>) => {
      track('staff_glm_flow_created', { staff_glm_flow_created: props });
    },
    [track],
  );

  const trackGLMFlowUpdated = useCallback(
    (props: NonNullable<StaffEventProperties['staff_glm_flow_updated']>) => {
      track('staff_glm_flow_updated', { staff_glm_flow_updated: props });
    },
    [track],
  );

  const trackGLMFlowDeleted = useCallback(
    (props: NonNullable<StaffEventProperties['staff_glm_flow_deleted']>) => {
      track('staff_glm_flow_deleted', { staff_glm_flow_deleted: props });
    },
    [track],
  );

  const trackGLMFlowToggled = useCallback(
    (props: NonNullable<StaffEventProperties['staff_glm_flow_toggled']>) => {
      track('staff_glm_flow_toggled', { staff_glm_flow_toggled: props });
    },
    [track],
  );

  const trackAthleteGLMLinked = useCallback(
    (props: NonNullable<StaffEventProperties['staff_athlete_glm_linked']>) => {
      track('staff_athlete_glm_linked', { staff_athlete_glm_linked: props });
    },
    [track],
  );

  const trackAthleteGLMUnlinked = useCallback(
    (props: NonNullable<StaffEventProperties['staff_athlete_glm_unlinked']>) => {
      track('staff_athlete_glm_unlinked', { staff_athlete_glm_unlinked: props });
    },
    [track],
  );

  const trackAthleteGLMContactCreated = useCallback(
    (props: NonNullable<StaffEventProperties['staff_athlete_glm_contact_created']>) => {
      track('staff_athlete_glm_contact_created', { staff_athlete_glm_contact_created: props });
    },
    [track],
  );

  const trackAthleteGLMLinkRetried = useCallback(
    (props: NonNullable<StaffEventProperties['staff_athlete_glm_link_retried']>) => {
      track('staff_athlete_glm_link_retried', { staff_athlete_glm_link_retried: props });
    },
    [track],
  );

  const trackAthleteGLMLinkIconClicked = useCallback(
    (props: NonNullable<StaffEventProperties['staff_athlete_glm_link_icon_clicked']>) => {
      track('staff_athlete_glm_link_icon_clicked', { staff_athlete_glm_link_icon_clicked: props });
    },
    [track],
  );

  const trackGLMRecordsModalViewed = useCallback(
    (props: NonNullable<StaffEventProperties['staff_glm_records_modal_viewed']>) => {
      track('staff_glm_records_modal_viewed', { staff_glm_records_modal_viewed: props });
    },
    [track],
  );

  const trackGLMRecordsModalFiltered = useCallback(
    (props: NonNullable<StaffEventProperties['staff_glm_records_modal_filtered']>) => {
      track('staff_glm_records_modal_filtered', { staff_glm_records_modal_filtered: props });
    },
    [track],
  );

  const trackOnboardingChecklistStepUpdated = useCallback(
    (props: NonNullable<StaffEventProperties['staff_onboarding_checklist_step_updated']>) => {
      track('staff_onboarding_checklist_step_updated', {
        staff_onboarding_checklist_step_updated: props,
      });
    },
    [track],
  );

  const trackOnboardingChecklistBannerDismissed = useCallback(
    (props: NonNullable<StaffEventProperties['staff_onboarding_checklist_banner_dismissed']>) => {
      track('staff_onboarding_checklist_banner_dismissed', {
        staff_onboarding_checklist_banner_dismissed: props,
      });
    },
    [track],
  );

  const trackStripeDashboardViewed = useCallback(
    (props: NonNullable<StaffEventProperties['staff_stripe_dashboard_viewed']>) => {
      track('staff_stripe_dashboard_viewed', { staff_stripe_dashboard_viewed: props });
    },
    [track],
  );

  const trackAthleteSelected = useCallback(
    (props: NonNullable<StaffEventProperties['staff_athlete_selected']>) => {
      track('staff_athlete_selected', { staff_athlete_selected: props });
    },
    [track],
  );

  const trackAthleteListPanelToggled = useCallback(
    (props: NonNullable<StaffEventProperties['staff_athlete_list_panel_toggled']>) => {
      track('staff_athlete_list_panel_toggled', { staff_athlete_list_panel_toggled: props });
    },
    [track],
  );

  const trackCalendarViewChanged = useCallback(
    (props: NonNullable<StaffEventProperties['staff_calendar_view_changed']>) => {
      track('staff_calendar_view_changed', { staff_calendar_view_changed: props });
    },
    [track],
  );

  const trackAssignWorkoutOpened = useCallback(
    (props: NonNullable<StaffEventProperties['staff_assign_workout_opened']>) => {
      track('staff_assign_workout_opened', { staff_assign_workout_opened: props });
    },
    [track],
  );

  const trackAssignDateChanged = useCallback(
    (props: NonNullable<StaffEventProperties['staff_assign_date_changed']>) => {
      track('staff_assign_date_changed', { staff_assign_date_changed: props });
    },
    [track],
  );

  const trackAssignWorkoutConfirmed = useCallback(
    (props: NonNullable<StaffEventProperties['staff_assign_workout_confirmed']>) => {
      track('staff_assign_workout_confirmed', { staff_assign_workout_confirmed: props });
    },
    [track],
  );

  const trackAssignWorkoutCancelled = useCallback(() => {
    track('staff_assign_workout_cancelled', { staff_assign_workout_cancelled: {} });
  }, [track]);

  const trackWorkoutCardClicked = useCallback(
    (props: NonNullable<StaffEventProperties['staff_workout_card_clicked']>) => {
      track('staff_workout_card_clicked', { staff_workout_card_clicked: props });
    },
    [track],
  );

  const trackWorkoutVisibilityQuickToggled = useCallback(
    (props: NonNullable<StaffEventProperties['staff_workout_visibility_quick_toggled']>) => {
      track('staff_workout_visibility_quick_toggled', { staff_workout_visibility_quick_toggled: props });
    },
    [track],
  );

  const trackWorkoutVisibilityModalToggled = useCallback(
    (props: NonNullable<StaffEventProperties['staff_workout_visibility_modal_toggled']>) => {
      track('staff_workout_visibility_modal_toggled', { staff_workout_visibility_modal_toggled: props });
    },
    [track],
  );

  const trackWorkoutEditClicked = useCallback(
    (props: NonNullable<StaffEventProperties['staff_workout_edit_clicked']>) => {
      track('staff_workout_edit_clicked', { staff_workout_edit_clicked: props });
    },
    [track],
  );

  const trackWorkoutDeleted = useCallback(
    (props: NonNullable<StaffEventProperties['staff_workout_deleted']>) => {
      track('staff_workout_deleted', { staff_workout_deleted: props });
    },
    [track],
  );

  const trackPayRateCreated = useCallback(
    (props: NonNullable<StaffEventProperties['staff_pay_rate_created']>) => {
      track('staff_pay_rate_created', { staff_pay_rate_created: props });
    },
    [track],
  );

  const trackPayRateUpdated = useCallback(
    (props: NonNullable<StaffEventProperties['staff_pay_rate_updated']>) => {
      track('staff_pay_rate_updated', { staff_pay_rate_updated: props });
    },
    [track],
  );

  const trackTotalSessionValueBreakdownViewed = useCallback(
    (props: NonNullable<StaffEventProperties['staff_total_session_value_breakdown_viewed']>) => {
      track('staff_total_session_value_breakdown_viewed', {
        staff_total_session_value_breakdown_viewed: props,
      });
    },
    [track],
  );

  return {
    trackAthleteSelected,
    trackAthleteListPanelToggled,
    trackCalendarViewChanged,
    trackAssignWorkoutOpened,
    trackAssignDateChanged,
    trackAssignWorkoutConfirmed,
    trackAssignWorkoutCancelled,
    trackWorkoutCardClicked,
    trackWorkoutVisibilityQuickToggled,
    trackWorkoutVisibilityModalToggled,
    trackWorkoutEditClicked,
    trackWorkoutDeleted,
    trackAthleteCreate,
    trackKioskCheckin,
    trackKioskAccessed,
    trackEventCreate,
    trackEventUpdate,
    trackEventBulkCreated,
    trackEventSessionRowChanged,
    trackEventCreateValidationFailed,
    trackEventCreateAbandoned,
    trackcalendarFilterApplied,
    trackInvoiceFilterApplied,
    trackAthleteReserved,
    trackPackageCreated,
    trackAthleteCheckedIn,
    trackAthleteLateCancelNoShow,
    trackReservationAlertBadgeViewed,
    trackLateCancelNoShowModalOpened,
    trackLateCancelNoShowModalDismissed,
    trackProductSalePurchased,
    trackAthleteUpdated,
    trackAthletePackageCreated,
    trackAthletePackageAutoPayUpdated,
    trackInvoiceApplyScopeSelected,
    trackPaymentMethodAutoPaySelected,
    trackInvoicePaymentDateEdited,
    trackInvoiceVoided,
    // Discounts
    trackDiscountCreated,
    trackDiscountUpdated,
    trackDiscountArchived,
    trackDiscountUnarchived,
    trackDiscountUsageReportViewed,
    trackDiscountApplied,
    // Committed Club
    trackCheckInProgramCreated,
    trackCheckInProgramSettingsUpdated,
    trackCheckInProgramTrackingViewed,
    trackCheckInProgramTrackingFilterApplied,
    trackCheckInProgramTrackingCsvDownloaded,
    // Tap-to-Pay
    trackTapToPaySelected,
    trackTapToPayCollectionStarted,
    trackTapToPayPaymentSucceeded,
    trackTapToPayPaymentFailed,
    trackTapToPayCardSaveStarted,
    trackTapToPayCardSaved,
    trackTapToPayCardSaveFailed,
    trackTapToPayCancelled,
    trackTapToPayChangePaymentMethodClicked,
    trackTapToPayRetryClicked,
    trackTapToPayPollingTimedOut,
    // Card reader registration
    trackCardReaderPurchaseClicked,
    trackCardReaderRegistered,
    trackCardReaderRegistrationFailed,
    // AI Connect (MCP)
    trackAIConnectAuthorizeViewed,
    trackAIConnectAuthorized,
    trackAIConnectDenied,
    trackAIConnectRevoked,
    // Leaderboard
    trackLeaderboardToggled,
    trackLeaderboardFilterSheetOpened,
    trackLeaderboardDateFilterChanged,
    trackLeaderboardGenderFilterChanged,
    trackLeaderboardCategoryFilterChanged,
    trackLeaderboardFilterSheetApplied,
    trackLeaderboardEntryClicked,

    // Workout Tracker
    trackWorkoutHistoryViewed,
    trackWorkoutHistoryMovementOpened,
    trackWorkoutHistoryWorkoutOpened,
    // GLM flows
    trackGLMFlowCreated,
    trackGLMFlowUpdated,
    trackGLMFlowDeleted,
    trackGLMFlowToggled,
    // GLM linking
    trackAthleteGLMLinked,
    trackAthleteGLMUnlinked,
    trackAthleteGLMContactCreated,
    trackAthleteGLMLinkRetried,
    trackAthleteGLMLinkIconClicked,
    trackGLMRecordsModalViewed,
    trackGLMRecordsModalFiltered,
    // Stripe dashboard
    trackStripeDashboardViewed,
    // Onboarding checklist
    trackOnboardingChecklistStepUpdated,
    trackOnboardingChecklistBannerDismissed,
    // Payroll
    trackPayRateCreated,
    trackPayRateUpdated,
    trackTotalSessionValueBreakdownViewed,
  };
};
